import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../../utils/pricing';

// Navy chapter band (same grammar as home/page.tsx NavyBand). Band = divider (#31) / no repeated heading right after a band (#33).
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== House Steward detail (EN) — NEW page, faithful EN mirror of JA house-steward (Implementer A scope). =====
// English only, no Japanese characters. Design base: about/overview + home + JA house-steward.
// Facts from JA house-steward working tree + JA hub pricing + mng-thebutler/00 L78. No fabrication.
// NOTE: router/config.tsx registration for /en/services/house-steward/ is handled by another owner (not edited here).

export default function EnHouseStewardPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Butler Services', href: '/en/butler-services/' },
    { label: 'House Steward' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: '24 Hours', t: 'Around the Clock, 365 Days', d: 'Late-night requests, early-morning preparation, special weekend needs—a butler is always on standby. The phrase "outside service hours" does not exist in your life.' },
    { en: 'Team of Three', t: 'Specialization Through a Team of Three', d: 'The optimal butler is placed in each time band, delivering the highest quality from breakfast through business support to dinner. Sudden leave or illness is covered by the team, so service never lapses.' },
    { en: 'Personalized', t: 'Fully Personalized', d: 'Three butlers grasp every aspect of your life, understanding your preferences, habits, and values. We realize the true butler service in which needs are met before they are spoken.' },
    { en: 'Security', t: 'Highest-Standard Security', d: 'With a butler always present around the clock, there is great peace of mind for security—handling suspicious visitors, emergency contact, and confirming important guests.' },
    { en: 'Whole Family', t: 'Support for the Whole Family', d: 'We comprehensively support the entire family—the head’s business, social activities, children’s education, and care for elderly members—coordinating and optimizing all of it.' },
  ];

  const shifts = [
    { icon: 'AM', t: 'Morning Butler', time: '8:00 – 16:00', d: 'Covers early morning to afternoon: wake-up support, breakfast, morning housework, lunch preparation, and afternoon planning—perfectly ordering the start of your day.' },
    { icon: 'PM', t: 'Daytime Butler', time: '13:00 – 21:00', d: 'Covers the most active hours from midday to evening: guest reception, business support, outing preparation, shopping, dinner preparation, and dinner service. Takes over seamlessly from the morning butler.' },
    { icon: 'PN', t: 'Night Butler', time: '20:00 – 8:00 next day', d: 'Covers night through early morning: after-dinner cleanup, bedtime preparation, nighttime patrol, late-night requests, and early-morning prep—so you can rest in comfort.' },
  ];

  const scenes = [
    { t: 'Very Busy Executives & Public Figures', d: 'For top executives who wish to entrust all of life to the butler team and focus fully on business or creative work—freed from every task from early morning to late night to spend time only on what truly matters.' },
    { t: 'Family Office Operations', d: 'For affluent families managing multiple businesses and investments, integrating both residence and office—coordinating schedules, receiving guests, managing important documents, and providing secretarial functions.' },
    { t: 'A Japan Base for International VIPs', d: 'As a base for global VIPs frequently traveling between Japan and abroad—handling communication across time zones and responding instantly to sudden changes, with a 24-hour-ready environment.' },
    { t: 'Large Estates & Multiple Properties', d: 'For those owning vast estates or multiple properties—24-hour property management, staff supervision, security management, and arranging maintenance contractors.' },
  ];

  const serviceGroups = [
    { t: 'Complete Life Management', d: 'General housework, meals (breakfast, lunch, dinner, late-night), shopping, laundry, cleaning, organizing, pet care, plant care, seasonal response.' },
    { t: 'Personal Assistant Functions', d: 'Schedule management, reservation proxy, travel arrangement, gift arrangement, wardrobe management, important document filing, mail management.' },
    { t: 'Business & Secretarial Support', d: 'Guest reception (24 hours), meeting preparation, phone handling, email-check assistance, document sorting, office environment management.' },
    { t: 'Health & Wellness Support', d: 'Medication management, appointment accompaniment, health observation, nutrition management, exercise support, coordination with medical institutions.' },
    { t: 'Security & Estate Management', d: '24-hour watch, response to suspicious visitors, equipment inspection, repair arrangement, contractor attendance, emergency response.' },
    { t: 'Family & Social Support', d: 'Coordinating the whole family’s schedules, children’s education support, elderly care, home-party planning and execution, social calendar management.' },
  ];

  const faqs = [
    ['Why a three-butler team?', 'To provide the highest quality 24/7/365. The team achieves the continuity and specialization impossible for a single butler.'],
    ['What happens on a butler’s day off?', 'The three rotate their time off, so from your perspective a butler is always available. Service never lapses.'],
    ['Is my privacy protected?', 'Every butler signs a strict confidentiality agreement and follows the association’s code of ethics. Regular confidentiality training is also conducted.'],
    ['Is a long-term contract required?', 'We recommend three months or more, but one-month contracts are also possible. About three months is the guide for the team to fully understand you.'],
    ['How do you coordinate with other staff (drivers, gardeners)?', 'The butler team supervises all staff and achieves seamless coordination, responding instantly to sudden changes and special requests.'],
  ];

  const related = [
    { name: 'Footman', cad: 'Spot use from 3 hours', price: yenInc(jbaDispatchExTax.footmanBase), unit: '~ (incl. tax)', href: '/en/services/footman/' },
    { name: 'Butler', cad: '8 hours per day, dedicated', price: yenInc(jbaDispatchExTax.butlerBase), unit: '~ (incl. tax)', href: '/en/services/butler/' },
    { name: 'Second Steward', cad: '20 days per month, continuous', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '~ (incl. tax)', href: '/en/services/second-steward/' },
  ];

  return (
    <Layout>
      <title>House Steward | 24-Hour Resident Butler Service & Pricing | Japan Butler Association</title>
      <meta name="description" content={`THE BUTLER's House Steward places three master butlers on 8-hour rotation for 24/7/365 residence—managing the entire estate for VVIPs, ultra-high-net-worth clients, and family offices. ¥${yenInc(jbaDispatchExTax.houseStewardMonthly)} (incl. tax, monthly).`} />
      <link rel="canonical" href="https://butler-association.jp/en/services/house-steward" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-p-043-svc-housesteward.webp" alt="House Steward service. Master butlers managing an estate at the highest level" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.94)_0%,rgba(16,20,26,0.74)_48%,rgba(16,20,26,0.38)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan IV — House Steward</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.1vw,40px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">Around the clock, three butlers</span>
              <span className="block">managing the estate</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                "House Steward" is a proprietary THE BUTLER plan name, coined in homage to the steward—the most senior household officer, placed only in very large estates and responsible for personnel, accounts, correspondence, and travel arrangements.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                Historically the steward was a single occupation, not a name for a three-butler rotation.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                As the pinnacle plan of the Japan Butler Association, three master butlers rotate in 8-hour shifts to stand by 24 hours a day, responding instantly to every need.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                For VVIPs, ultra-high-net-worth clients, and family offices, we deliver an ultimate partnership that manages the entire estate—without interruption.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="About This Service" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 Overview */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              Around the clock, an <span className="text-[#a38e65]">ultimate lifestyle</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <div className="space-y-5">
                <p>House Steward is complete lifestyle management that transcends the concept of butler service. With specialized butlers optimized for morning, day, and night rotating in 8-hour shifts, you receive the highest quality service anytime, 24/7/365.</p>
                <p>This is not merely staffing—it is an ultimate partnership that supports your very life.</p>
              </div>
              <div className="space-y-5">
                <p>By forming a team of three, we achieve continuity and specialization impossible for an individual.</p>
                <p>Each covers the hours they excel in, and thorough team meetings share information completely—delivering seamless service as if one butler were with you around the clock.</p>
              </div>
              <div className="space-y-5">
                <p>As the culmination of more than 130 years of butler education cultivated by the Japan Butler Association, only the finest talent, centered on Master Butler holders, takes part.</p>
                <p>They are not only technically excellent but embody dignity, refinement, insight, and above all a servant’s heart—true professionals.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.t} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.t}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="The Three Shifts" en="24-Hour Rotation" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 The Three Shifts (navy panel) */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              A perfect three-butler rotation
            </h2>
            <div className="grid lg:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start">
              <img src="/images/gen2/service-en-house-steward-rotation-01.jpg" alt="Three Japanese butlers reviewing a rotation handover for continuous House Steward service" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
              <div className="bg-[#1a2942] p-7 sm:p-9">
                <div className="space-y-6">
                  {shifts.map((s) => (
                    <div key={s.t} className="flex gap-5 border-b border-white/10 pb-6 last:border-0 last:pb-0">
                      <span className="shrink-0 w-11 h-11 border border-[#a38e65]/60 flex items-center justify-center font-['Playfair_Display',serif] text-[#d7b779] text-sm">{s.icon}</span>
                      <div>
                        <div className="flex flex-wrap items-baseline gap-x-4 mb-1.5">
                          <h3 className="jba-display font-medium text-[#f8f6f0] text-lg">{s.t}</h3>
                          <span className="font-['Playfair_Display',serif] text-[#d7b779] text-sm tracking-[0.08em]">{s.time}</span>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">{s.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-11 h-px bg-[#a38e65] mt-7 mb-5"></div>
                <ul className="space-y-2 font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.8]">
                  <li>Each butler sets a one-hour handover for complete information sharing.</li>
                  <li>A weekly team meeting continually raises service quality.</li>
                  <li>In emergencies, all three respond immediately.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="Usage Scenes" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 Scenes */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              For those who seek the highest standard
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/gen2/service-en-house-steward-estate-management-01.jpg" alt="A Japanese House Steward checking a floor plan and tablet for estate operations" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="What We Provide" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 What We Do */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Complete support for every aspect of life
            </h2>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                {serviceGroups.map((g) => (
                  <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">{g.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{g.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/gen2/service-en-house-steward-dining-coordination-01.jpg" alt="Two Japanese House Steward butlers coordinating formal dining service together" className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="Pricing" en="Fees" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 Pricing */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Pricing
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[34px] text-[#f8f6f0] leading-none">&yen;{yenInc(jbaDispatchExTax.houseStewardMonthly)}</div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">incl. tax &middot; monthly</p>
                <p className="font-['Noto_Serif_JP',serif] text-xs text-[#ded8cc] mt-4 leading-[1.7]">24 hours &times; 30 days<br />three-butler rotation</p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['Monthly Rate', `¥${yenInc(jbaDispatchExTax.houseStewardMonthly)} (incl. tax)`],
                    ['Contract Term', '3 months or more recommended (1-month contracts possible)'],
                    ['Butler Selection', 'Free (all three may be named)'],
                    ['Transportation', 'Included in the monthly flat rate'],
                    ['Initial Fee', 'Setup fee ¥500,000 (first time only: estate survey, system build, team training)'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] shrink-0">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[16px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mt-6">
                  <span className="text-[#8f7a56]">Payment:</span> bank transfer (monthly, quarterly, or annual). 3% off for 6-month prepayment, 5% off for annual prepayment.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="Frequently Asked Questions" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Frequently asked questions
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VII" ja="Other Plans" en="More Options" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 Other Plans */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Compare the plans
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              For a first trial choose Footman; for a special full day, Butler; for 20 days of monthly continuity, Second Steward.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">&yen;{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/en/butler-services/" className={BTN08_ON_LIGHT}>Back to all services</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S8 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">Experience the ultimate butler service</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                We consult in strict confidence. When you contact us, please mention "House Steward." At the first meeting, the Representative Director will visit in person for a detailed consultation.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">Phone: 03-6824-4086 (Weekdays 10:00-18:00)</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
